import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Search, Filter, Rss } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { SourceCard, SourceCardSkeleton, type RSSSource } from "./SourceCard";

interface SourceGridProps {
  sources: RSSSource[];
  isLoading: boolean;
  onToggle: (id: string, enabled: boolean) => void;
  onEdit: (source: RSSSource) => void;
  onDelete: (source: RSSSource) => void;
  onRefresh: (source: RSSSource) => void;
  onAddSource: (source: Omit<RSSSource, "id" | "articleCount" | "lastFetchedAt" | "status">) => void;
}

type TierFilter = "all" | "1" | "2";
type StatusFilter = "all" | "active" | "error" | "paused";

export function SourceGrid({
  sources,
  isLoading,
  onToggle,
  onEdit,
  onDelete,
  onRefresh,
  onAddSource,
}: SourceGridProps) {
  const [search, setSearch] = useState("");
  const [tierFilter, setTierFilter] = useState<TierFilter>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newSource, setNewSource] = useState({
    name: "",
    url: "",
    tier: 1 as 1 | 2,
    isEnabled: true,
  });

  // Filter sources
  const filteredSources = sources.filter((source) => {
    const matchesSearch =
      source.name.toLowerCase().includes(search.toLowerCase()) ||
      source.url.toLowerCase().includes(search.toLowerCase());
    const matchesTier = tierFilter === "all" || source.tier.toString() === tierFilter;
    const matchesStatus = statusFilter === "all" || source.status === statusFilter;
    return matchesSearch && matchesTier && matchesStatus;
  });

  // Stats
  const activeCount = sources.filter((s) => s.status === "active").length;
  const errorCount = sources.filter((s) => s.status === "error").length;
  const totalArticles = sources.reduce((sum, s) => sum + s.articleCount, 0);

  const handleAddSource = () => {
    if (newSource.name && newSource.url) {
      onAddSource(newSource);
      setNewSource({ name: "", url: "", tier: 1, isEnabled: true });
      setIsAddDialogOpen(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats bar */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="grid grid-cols-2 sm:grid-cols-4 gap-4"
      >
        <div className="p-4 rounded-lg bg-muted/50 border">
          <p className="text-2xl font-semibold">{sources.length}</p>
          <p className="text-sm text-muted-foreground">Total Sources</p>
        </div>
        <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
          <p className="text-2xl font-semibold text-emerald-600 dark:text-emerald-400">
            {activeCount}
          </p>
          <p className="text-sm text-muted-foreground">Active</p>
        </div>
        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
          <p className="text-2xl font-semibold text-red-600 dark:text-red-400">
            {errorCount}
          </p>
          <p className="text-sm text-muted-foreground">Errors</p>
        </div>
        <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
          <p className="text-2xl font-semibold text-primary">
            {totalArticles.toLocaleString()}
          </p>
          <p className="text-sm text-muted-foreground">Total Articles</p>
        </div>
      </motion.div>

      {/* Filters and actions */}
      <div className="flex flex-col sm:flex-row gap-3 justify-between">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search sources..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-10 bg-background"
            />
          </div>

          {/* Tier filter */}
          <Select value={tierFilter} onValueChange={(v) => setTierFilter(v as TierFilter)}>
            <SelectTrigger className="w-[130px] h-10 bg-background">
              <Filter className="h-4 w-4 mr-2 text-muted-foreground" />
              <SelectValue placeholder="Tier" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Tiers</SelectItem>
              <SelectItem value="1">Tier 1</SelectItem>
              <SelectItem value="2">Tier 2</SelectItem>
            </SelectContent>
          </Select>

          {/* Status filter */}
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
            <SelectTrigger className="w-[130px] h-10 bg-background">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="error">Error</SelectItem>
              <SelectItem value="paused">Paused</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Add source button */}
        <Button onClick={() => setIsAddDialogOpen(true)} className="h-10">
          <Plus className="h-4 w-4 mr-2" />
          Add Source
        </Button>
      </div>

      {/* Source grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {isLoading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <SourceCardSkeleton key={i} />
          ))
        ) : filteredSources.length === 0 ? (
          <div className="col-span-full flex flex-col items-center justify-center py-16 text-center">
            <Rss className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <h3 className="font-medium text-muted-foreground">No sources found</h3>
            <p className="text-sm text-muted-foreground/70 mt-1">
              {sources.length === 0
                ? "Add your first RSS source to get started"
                : "Try adjusting your filters"}
            </p>
            {sources.length === 0 && (
              <Button onClick={() => setIsAddDialogOpen(true)} className="mt-4">
                <Plus className="h-4 w-4 mr-2" />
                Add Source
              </Button>
            )}
          </div>
        ) : (
          <AnimatePresence mode="popLayout">
            {filteredSources.map((source, index) => (
              <SourceCard
                key={source.id}
                source={source}
                onToggle={onToggle}
                onEdit={onEdit}
                onDelete={onDelete}
                onRefresh={onRefresh}
                delay={index}
              />
            ))}
          </AnimatePresence>
        )}
      </div>

      {/* Add Source Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add RSS Source</DialogTitle>
            <DialogDescription>
              Add a new RSS feed source to fetch articles from.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="source-name">Source Name</Label>
              <Input
                id="source-name"
                placeholder="e.g., The New York Times"
                value={newSource.name}
                onChange={(e) => setNewSource((s) => ({ ...s, name: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="source-url">Feed URL</Label>
              <Input
                id="source-url"
                placeholder="https://example.com/rss"
                value={newSource.url}
                onChange={(e) => setNewSource((s) => ({ ...s, url: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="source-tier">Tier</Label>
              <Select
                value={newSource.tier.toString()}
                onValueChange={(v) => setNewSource((s) => ({ ...s, tier: parseInt(v) as 1 | 2 }))}
              >
                <SelectTrigger id="source-tier">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Tier 1 - Primary</SelectItem>
                  <SelectItem value="2">Tier 2 - Secondary</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Tier 1 sources are prioritized for headline stories.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddSource}
              disabled={!newSource.name || !newSource.url}
            >
              Add Source
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
